import { useState, useCallback, useMemo } from "react";
import { Column } from "ui/components/Table/TableSearch/types";

export interface ColumnState {
  key: string;
  title: string;
  visible: boolean;
  alwaysVisible: boolean;
}

const getStorageKey = (storageKey: string) =>
  `columnVisibility:${storageKey}`;

const loadVisibility = (
  storageKey: string
): Record<string, boolean> | null => {
  try {
    const stored = localStorage.getItem(getStorageKey(storageKey));
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const saveVisibility = (
  storageKey: string,
  visibility: Record<string, boolean>
) => {
  try {
    localStorage.setItem(
      getStorageKey(storageKey),
      JSON.stringify(visibility)
    );
  } catch {
    // localStorage not available
  }
};

export const useColumnVisibility = (
  columns: Column[],
  storageKey: string
) => {
  const [visibility, setVisibility] = useState<Record<string, boolean>>(
    () => {
      const stored = loadVisibility(storageKey);
      if (stored) return stored;

      const initial: Record<string, boolean> = {};
      columns.forEach((col) => {
        if (col.key) {
          initial[col.key] =
            col.defaultVisible !== undefined ? col.defaultVisible : true;
        }
      });
      return initial;
    }
  );

  const toggleColumn = useCallback(
    (key: string) => {
      setVisibility((prev) => {
        const next = { ...prev, [key]: !prev[key] };
        saveVisibility(storageKey, next);
        return next;
      });
    },
    [storageKey]
  );

  const visibleColumns = useMemo(
    () =>
      columns.filter((col) => {
        if (!col.key) return true;
        if (col.alwaysVisible) return true;
        return visibility[col.key] !== false;
      }),
    [columns, visibility]
  );

  const columnStates: ColumnState[] = useMemo(
    () =>
      columns
        .filter((col) => col.key && col.title)
        .map((col) => ({
          key: col.key!,
          title: col.title,
          visible: col.alwaysVisible
            ? true
            : visibility[col.key!] !== false,
          alwaysVisible: !!col.alwaysVisible,
        })),
    [columns, visibility]
  );

  return { visibleColumns, columnStates, toggleColumn };
};
